import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { BookOpen, Plus, Search } from "lucide-react"
import { type FormEvent, useMemo, useState } from "react"
import { useNavigate } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import {
  type DocumentoResumen,
  NOMBRE_TIPO,
  TIPOS_DOC,
  type TipoDoc,
  TONO_ESTADO,
} from "@/componentes/documentos/tipos"
import { KpiBiblioteca } from "@/componentes/kpi/KpiPlanificacion"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * La biblioteca del SGI: los documentos controlados de la agencia.
 *
 * ⚠ **No es el Drive.** Aquí se REDACTAN documentos dentro del sistema y lo que los
 * define es su ciclo de vida —código, versión, vigente u obsoleto y tres firmas—; el
 * Drive guarda archivos que alguien sube. Meter esto allí convertiría el almacén de
 * archivos en un gestor documental a medias para todo lo que se guarda.
 *
 * ⚠ **El código no se teclea: lo acuña el servidor.** `SETIECA-SGI-MAN-001` sale del
 * prefijo de la empresa más el tipo y un número que se asigna por tipo. Pedirlo a mano
 * garantiza duplicados el día que dos personas creen un documento a la vez.
 *
 * ⚠ **Se agrupa por tipo y no se pagina.** Una agencia tiene decenas de documentos
 * controlados, no miles; lo que se busca es «el procedimiento de nacionalización», y
 * eso se encuentra antes recorriendo cinco grupos cortos que pasando páginas.
 */

export function Biblioteca() {
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "biblioteca:editar")

  const [busqueda, setBusqueda] = useState("")
  const [creando, setCreando] = useState(false)
  const [tipo, setTipo] = useState<TipoDoc>("POL")
  const [titulo, setTitulo] = useState("")

  const documentos = useQuery({
    queryKey: ["biblioteca", "documentos"],
    queryFn: () => api.get<DocumentoResumen[]>("/api/v1/biblioteca/documentos"),
  })

  const crear = useMutation({
    mutationFn: () => api.post<DocumentoResumen>("/api/v1/biblioteca/documentos", { tipo, titulo }),
    onSuccess: (nuevo) => {
      clienteQuery.invalidateQueries({ queryKey: ["biblioteca", "documentos"] })
      navegar(`/planificacion/biblioteca/${nuevo.id}`)
    },
  })

  const grupos = useMemo(() => {
    const texto = busqueda.trim().toLowerCase()
    const lista = (documentos.data ?? []).filter(
      (d) => !texto || `${d.codigo} ${d.titulo}`.toLowerCase().includes(texto),
    )
    return TIPOS_DOC.map((t) => [t, lista.filter((d) => d.tipo === t)] as const).filter(
      ([, l]) => l.length > 0,
    )
  }, [documentos.data, busqueda])

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (titulo.trim()) crear.mutate()
  }

  return (
    <div>
      {/* Ni título ni frase propios: los pone la cabecera de «Planificación
          estratégica». Aquí arriba sólo van las acciones, a la derecha. */}
      {puedeEditar && (
        <div className="mb-4 flex justify-end">
          <Boton variante="primario" onClick={() => setCreando(true)}>
            <Plus size={16} aria-hidden="true" />
            Nuevo documento
          </Boton>
        </div>
      )}
      <KpiBiblioteca />

      <div
        className="mb-6 flex items-center gap-2 rounded-[var(--radius-control)] border px-3 py-2"
        style={{ borderColor: "var(--border)" }}
      >
        <Search size={16} aria-hidden="true" style={{ color: "var(--fg-muted)" }} />
        <input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por código o título…"
          aria-label="Buscar en la biblioteca"
          className="w-full bg-transparent text-sm outline-none"
        />
      </div>

      {documentos.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando la biblioteca…
        </p>
      )}

      {documentos.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar la biblioteca
        </p>
      )}

      {documentos.data && grupos.length === 0 && (
        <div
          className="flex flex-col items-center gap-2 superficie py-12"
          style={{ color: "var(--fg-muted)" }}
        >
          <BookOpen size={22} aria-hidden="true" />
          <p className="text-sm">
            {busqueda
              ? "Ningún documento coincide con esa búsqueda"
              : "Todavía no hay documentos controlados"}
          </p>
        </div>
      )}

      <div className="space-y-8">
        {grupos.map(([tipoGrupo, lista]) => (
          <section key={tipoGrupo}>
            <h2
              className="mb-3 text-xs font-semibold uppercase tracking-wide"
              style={{ color: "var(--fg-muted)" }}
            >
              {NOMBRE_TIPO[tipoGrupo]}
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {lista.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => navegar(`/planificacion/biblioteca/${doc.id}`)}
                  className="superficie p-4 text-left toque-cristal"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="font-mono text-[11px] font-semibold tabular"
                      style={{ color: "var(--primary-texto)" }}
                    >
                      {doc.codigo}
                    </span>
                    <Insignia tono={TONO_ESTADO[doc.estado]}>{doc.estado}</Insignia>
                  </div>
                  <p className="mt-2 text-sm font-semibold">{doc.titulo}</p>
                  <p className="mt-3 text-xs tabular" style={{ color: "var(--fg-muted)" }}>
                    v{doc.version}
                    {doc.versiones_publicadas > 0 &&
                      ` · ${doc.versiones_publicadas} publicada${doc.versiones_publicadas === 1 ? "" : "s"}`}
                  </p>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      <Modal
        abierto={creando}
        titulo="Nuevo documento controlado"
        descripcion="El código lo acuña el sistema con el prefijo de la empresa y un número por tipo."
        onCerrar={() => setCreando(false)}
        ancho="sm"
      >
        <form onSubmit={enviar}>
          <label htmlFor="tipo-doc" className="mb-1 block text-xs font-medium uppercase">
            Tipo
          </label>
          <select
            id="tipo-doc"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoDoc)}
            className="mb-4 h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--surface)",
              color: "var(--fg)",
            }}
          >
            {TIPOS_DOC.map((t) => (
              <option key={t} value={t}>
                {NOMBRE_TIPO[t]} ({t})
              </option>
            ))}
          </select>

          <label htmlFor="titulo-doc" className="mb-1 block text-xs font-medium uppercase">
            Título
          </label>
          <input
            id="titulo-doc"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            maxLength={255}
            className="mb-4 h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--surface)",
              color: "var(--fg)",
            }}
          />

          {crear.isError && (
            <p role="alert" className="mb-3 text-sm" style={{ color: "var(--destructive)" }}>
              {crear.error instanceof ErrorApi ? crear.error.message : "No se pudo crear"}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Boton variante="fantasma" onClick={() => setCreando(false)}>
              Cancelar
            </Boton>
            <Boton variante="primario" type="submit" disabled={crear.isPending}>
              {crear.isPending ? "Creando…" : "Crear"}
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  )
}
